import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../hooks/useLanguage";
import { Calendar } from "react-calendar";
import { toZonedTime } from 'date-fns-tz';

import { api } from "ticketino-api-client";
import {
    CreateShopBasketOrder, GetShopBasketOrder, AddTicketsToOrder, GetEventById, GetEventsThatAreInEventGroup, 
    CountEventsThatAreInEventGroup, GetTimeSlotsForEvent, GetTicketSoldOfEvent, GetTicketTypesByEventId, GetOrders, GetTicketsOfOrder
} from "../utils/api";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";

import "../css/calendar.css";

const Step30_Calendar = () => {
    const [regularToken, setRegularToken] = useState(null);
    const [bookingType, setBookingType] = useState(null);
    const [resources, setResources] = useState({});

    const [posId, setPosId] = useState(null);
    const [eventGroupId, setEventGroupId] = useState(0);
    const [eventGroupCityRallyId, setEventGroupCityRallyId] = useState(0);

    const [orderId, setOrderId] = useState(0);
    const [order, setOrder] = useState({});

    const [timeslotOrders, setTimeslotOrders] = useState(null);

    const [eventsWithDetails, setEventsWithDetails] = useState([]);

    const [eventDates, setEventDates] = useState([]);
    const [date, setDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState("");
    const [timeSlots, setTimeSlots] = useState([]);

    const [organizerIdWow, setorganizerIdWow] = useState("");

    const selectedTickets = JSON.parse(localStorage.getItem('selectedTickets'));
    const selectedAddOns = JSON.parse(localStorage.getItem('selectedTicketsAddons'));
    const selectedTicketsCrossSelling = JSON.parse(localStorage.getItem('selectedTicketsCrossSelling'));

    const timeZone = 'Europe/Zurich';
    const today = new Date();
    const zurichDate = toZonedTime(today, timeZone);   // Convert to Zurich time
    const defaultMonth = zurichDate.getMonth(); // Month based on Zurich time
    const [currentMonth, setCurrentMonth] = useState(new Date(zurichDate.getFullYear(), defaultMonth, 1));

    const [isLoading, setIsLoading] = useState(true);
    const [isBooking, setIsBooking] = useState(false);

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    // fetching from params
    const { language } = useLanguage();
    const username = localStorage.getItem("username");
    const adminNewBookingUsername = sessionStorage.getItem("adminNewBookingUsername");

    // Check if selected addons contain tour or guide
    const hasTourOrGuideAddon = useMemo(() => {
        if (!selectedAddOns || !selectedAddOns.tickets) return false;
        
        return selectedAddOns.tickets.some(ticket => {
            const code = (ticket.externalTicketCode || '').toLowerCase();
            return code.includes('tour') || code.includes('guide');
        });
    }, [selectedAddOns]);

    // Calculate the date 14 days from now (Zurich time)
    const minSelectableDate = useMemo(() => {
        if (!hasTourOrGuideAddon) return zurichDate;
        
        const dateIn14Days = new Date(zurichDate);
        dateIn14Days.setDate(dateIn14Days.getDate() + 14);
        return dateIn14Days;
    }, [hasTourOrGuideAddon, zurichDate]);

    useEffect(() => {
        setRegularToken(localStorage.getItem("token"));
        setBookingType(localStorage.getItem("bookingType"));
        requestFormSettings();
    }, []);

    useEffect(() => {
        requestFormSettings();
    }, [bookingType]);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (!posId) return;

        const initializeOrder = async () => {
            let orderIdFromSession = localStorage.getItem("OrderId");

            if (orderIdFromSession) {
                // Try to fetch the order
                try {
                    let orderResponse = await fetchOrderById(orderIdFromSession);
                    if (orderResponse && orderResponse.id) {
                        setOrderId(orderResponse.id);
                        setOrder(orderResponse);
                        return;
                    }
                } catch (err) {
                    // If fetch fails, continue to create a new order
                }
            }

            // Create a new order
            try {
                const res = await createOrder()
                await localStorage.setItem("OrderId", res.id);
                setOrderId(res.id);
                setOrder(res);
            } catch (err) {
                console.error("Failed to create order", err);
            }
        };

        initializeOrder();
    }, [posId, baseUrl]);

    useEffect(() => {
        if (currentMonth && eventGroupId > 0) {
            loadCalendarEventDates(eventGroupId, currentMonth);
        }
    }, [currentMonth, eventGroupId]);

    const selectedDateZurich = useMemo(() => {
        if (selectedDate) {
            // parse selectedDate as Zurich local date
            const zurichLocalDate = toZonedTime(new Date(selectedDate), timeZone);
            return new Date(zurichLocalDate.getTime());
        }
        return null;
    }, [selectedDate, timeZone]);

    useEffect(() => {
        (async () => {
            let timeslotsByEventIds = [];

            if (selectedDateZurich) {
                const currentEventsByDate = eventsWithDetails.filter(e => {
                    // Convert event start for comparison
                    const eventDateUTC = new Date(e.start);
                    // Compare only the date part for matching
                    return eventDateUTC.toISOString().split('T')[0] === selectedDate;
                });

                for (const event of currentEventsByDate) {
                    /*let timeslotsOfEvent = await fetchTimeSlotsByEventId(event.id);*/

                    const availableTimeslots = await getAvailableTimeslotsForEvent(event.id, selectedTickets, baseUrl, regularToken);

                    if (availableTimeslots && availableTimeslots.length > 0) {
                        // Map over timeslotsOfEvent to add the event id to each timeslot
                        const timeslotsWithEventId = availableTimeslots.map(timeslot => ({
                            ...timeslot,
                            eventId: event.id
                        }));

                        timeslotsByEventIds = [...timeslotsByEventIds, ...timeslotsWithEventId];
                    }
                }
            }

            setTimeSlots(timeslotsByEventIds);
        })();
    }, [selectedDateZurich]);

    useEffect(() => {
        if (selectedDate) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
    }, [selectedDate]);

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            setorganizerIdWow(res.data.organizerId);
            setPosId(res.data.posId);
            setEventGroupId(res.data.eventGroupId);
            setEventGroupCityRallyId(res.data.eventGroupCityRallyId);
        });
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    //Get all events by eventgroup id
    //Foreach events loop -> get all "ticket Types" by eventId and add to the event and "available timslots" also to the event
    //then filter these events by the ones that have externalCode
    //with the list of events then filter again with available TimeSlots
    const loadCalendarEventDates = async (eventGroupId, currentMonth) => {

        setIsLoading(true);

        // First day of the current month
        const dateFrom = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth(), 1));

        // Last day of the current month
        const dateTo = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0));

        const dateFromISO = dateFrom.toISOString().split('T')[0];
        const dateToISO = dateTo.toISOString().split('T')[0];

        const totalNumberOfEvents = await fetchCountEventsThatAreInEventGroup(eventGroupId, dateFromISO, dateToISO);
        const eventBatchSize = 100;
        let events = [];

        // Fetch events in batches of 'eventBatchSize' until all events in the group are loaded.
        // Each batch is requested with a skip value 'i' to paginate through the results.
        // The fetched events are concatenated into the 'events' array.
        for (let i = 0; i <= totalNumberOfEvents; i += eventBatchSize) {
            const eventGroupEventsResponse = await fetchEventsThatAreInEventGroup(eventGroupId, language, eventBatchSize, i, dateFromISO, dateToISO);
            events = events.concat(eventGroupEventsResponse?.events);
        }

        const eventsWithDetails = (
            await Promise.all(
                events.map(async (event) => {

                    // exclude events that are not active with status not equal to 1
                    const eventDetails = await fetchEventById(event.id);
                    if (eventDetails?.status !== 1) {
                        return null;
                    }

                    // exclude sold out event
                    const ticketCountResponse = await fetchTicketCount(event.id);
                    if ((eventDetails?.maxTickets - ticketCountResponse) <= 0) {
                        return null;
                    }

                    // get ticket types of event
                    const ticketTypesResponse = await GetTicketTypesByEventId(baseUrl, "token", regularToken, event.id);

                    // exclude ticket types without externalTicketCode
                    const filteredTicketTypes = (ticketTypesResponse?.ticketTypes || []).filter(tt => !!tt.externalTicketCode);

                    // if no ticketTypes with externalTicketCode, skip this event
                    if (filteredTicketTypes.length === 0) {
                        return null;
                    }

                    // get available timeslots of event
                    const timeslotsOfEvent = await fetchTimeSlotsByEventId(event.id);

                    return { ...event, ticketTypes: filteredTicketTypes, timeslotsOfEvent: timeslotsOfEvent };
                })
            )
        )
            .filter(e => e !== null) // filter out the null values here

        //loop thru the eventsWithDetails then match these selectedTickets by it's externalTicketCode with the event.ticketTypes.externalTicketCode, if doesnt find a match skip loop else put them into matchedSelectedTicketTypes
        //then loop the timeslotsOfevent filtered by timeslotsOfEvent.ticketTypes.item1 that exist in the matchedSelectedTicketTypes
        //if current timeslotOfEvent.totalAvailableTickets is >= the matchedSelectedTicketTypes push the datesArray.push(event.start?.split('T')[0]);
        const datesSet = new Set();

        const selectedTicketCodes = (selectedTickets?.tickets || [])
            .filter(t => !!t.externalTicketCode)
            .map(t => t.externalTicketCode);

        eventsWithDetails.forEach(event => {
            // Find ticket types in the event that match selectedTicketCodes
            const matchedSelectedTicketTypes = event.ticketTypes.filter(tt =>
                selectedTicketCodes.includes(tt.externalTicketCode)
            );

            // No matching ticket types, skip this event
            if (matchedSelectedTicketTypes.length === 0) {
                return;
            }

            // Get the ticketTypeIds from matched selected ticket types
            const matchedTicketTypeIds = matchedSelectedTicketTypes.map(tt => tt.id);

            // Loop through timeslots and check for availability
            event.timeslotsOfEvent.forEach(timeslot => {
                // Find ticketTypeIds in this timeslot
                const timeslotTicketTypeIds = timeslot.ticketTypes.map(tt => tt.item1);

                // Check if all matched ticket type ids are present in this timeslot
                const allMatchedPresent = matchedTicketTypeIds.every(id =>
                    timeslotTicketTypeIds.includes(id)
                );

                // Calculate total quantity tickets from selectedTickets (match by externalTicketCode)
                const totalQuantity = (selectedTickets?.tickets || [])
                    .filter(t => matchedSelectedTicketTypes.some(tt => tt.externalTicketCode === t.externalTicketCode))
                    .reduce((sum, t) => sum + (t.quantity || 0), 0);

                // Check if enough tickets are available
                const ticketsLeft = timeslot.totalAvailableTickets - timeslot.totalTicketsSold;
                const isSlotAvailable = ticketsLeft >= totalQuantity;

                // add calendar date if available
                if (allMatchedPresent && isSlotAvailable) {
                    const eventDate = event.start?.split('T')[0];
                    
                    // If tour or guide addon is selected, exclude dates within the first 14 days
                    if (hasTourOrGuideAddon) {
                        const eventDateObj = new Date(eventDate);
                        const minDate = new Date(minSelectableDate);
                        minDate.setHours(0, 0, 0, 0);
                        
                        if (eventDateObj >= minDate) {
                            datesSet.add(eventDate);
                        }
                    } else {
                        datesSet.add(eventDate);
                    }
                }
            });
        });

        const datesArray = Array.from(datesSet);

        setEventDates(datesArray);
        setEventsWithDetails(eventsWithDetails);
        setIsLoading(false);
    };

    // get event timeslot availability to display in the side overlay
    const getAvailableTimeslotsForEvent = async (eventId, selectedTickets) => {
        try {
            // Fetch orders for this event with their tickets
            const ordersWithTickets = await fetchOrdersByEventId(eventId);

            // Find the event details from eventsWithDetails
            const event = eventsWithDetails.find(e => e.id === eventId);
            if (!event || !event.ticketTypes) {
                return [];
            }

            // Get selected externalTicketCodes
            const selectedTicketCodes = (selectedTickets?.tickets || [])
                .filter(t => !!t.externalTicketCode)
                .map(t => t.externalTicketCode);

            // Find ticket types in the event that match selectedTicketCodes
            const matchedSelectedTicketTypes = event.ticketTypes.filter(tt =>
                selectedTicketCodes.includes(tt.externalTicketCode)
            );

            if (matchedSelectedTicketTypes.length === 0) {
                return [];
            }

            // Get the ticketTypeIds for matched ticket types
            const matchedTicketTypeIds = matchedSelectedTicketTypes.map(tt => tt.id);

            // Calculate total quantity from selectedTickets for these ticket types (match by externalTicketCode)
            const totalQuantity = (selectedTickets?.tickets || [])
                .filter(t => matchedSelectedTicketTypes.some(tt => tt.externalTicketCode === t.externalTicketCode))
                .reduce((sum, t) => sum + (t.quantity || 0), 0);

            // Use timeslots from event details
            const timeslotsOfEvent = event.timeslotsOfEvent || [];

            // Check for available timeslots with enough tickets for the requested quantity
            const availableTimeslots = timeslotsOfEvent.filter(ts => {
                const timeslotTicketTypeIds = ts.ticketTypes.map(tt => tt.item1);
                const allMatchedPresent = matchedTicketTypeIds.every(id =>
                    timeslotTicketTypeIds.includes(id)
                );
                
                if (!allMatchedPresent) {
                    return false;
                }

                // Check basic ticket availability
                const basicAvailability = ts.totalAvailableTickets >= totalQuantity;
                if (!basicAvailability) {
                    return false;
                }

                // Timeslot can be booked per max 3 Groups
                // Count how many orders have tickets matching this timeslot
                const ordersWithMatchingTimeslot = ordersWithTickets.filter(order => {
                    // Check if this order has at least one ticket that matches the timeslot time range
                    return (order.tickets || []).some(ticket => {
                        // Check if ticket has timeOfEntryFrom and timeOfEntryTo
                        if (!ticket.timeOfEntryFrom || !ticket.timeOfEntryTo) {
                            return false;
                        }

                        // Compare timeslot times with ticket times
                        // Both should match exactly for this timeslot
                        const timeslotBegin = ts.beginTime; 
                        const timeslotEnd = ts.endTime; 
                        const ticketFrom = ticket.timeOfEntryFrom;
                        const ticketTo = ticket.timeOfEntryTo;

                        // Check if ticket times match this timeslot
                        return ticketFrom === timeslotBegin && ticketTo === timeslotEnd;
                    });
                });

                // If 3 or more orders have tickets in this timeslot, mark as unavailable
                const orderCount = ordersWithMatchingTimeslot.length;
                
                // Timeslot is unavailable if 3 or more orders are using it
                if (orderCount >= 3) {
                    return false;
                }

                return true;
            });

            return availableTimeslots;
        } catch (err) {
            console.error('Error in getAvailableTimeslotsForEvent:', err);
            return [];
        }
    }

    const createOrder = async () => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at createOrder");
            }

            const newOrder = {
                affiliateShopId: null,
                currency: "CHF",
                tenantId: 1,
                pointOfSaleId: posId,
                abbreviation: "",
                paymentType: 1,
            };

            //when is admin placing a new order for a buyer user, use it as the order username
            if (adminNewBookingUsername && adminNewBookingUsername.length > 0) {
                newOrder.email = adminNewBookingUsername;
            } else if (username && username.length > 0) {
                //when user is logged in, set the email of the order to the username
                newOrder.email = username;
            }

            const res = await CreateShopBasketOrder(baseUrl, "token", regularToken, newOrder);
            return res;
        } catch (error) {
            console.error('Error creating order:', error);
        }
    };

    const fetchOrderById = async (orderId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchOrderById");
            }
            const res = await GetShopBasketOrder(baseUrl, "token", regularToken, orderId);
            return res;
        } catch (error) {
            console.error('Error fetching order by Id:', error);
        }
    };

    const fetchOrdersByEventId = async (eventId, numberToSkip = 0) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at fetchOrders");
            }

            setIsLoading(true);

            // Pass null for organizerId to filter by eventId only
            const data = await GetOrders(baseUrl, "token", regularToken, null, false, "", numberToSkip, eventId);
            
            // Attach tickets to orders
            const ordersWithTickets = await attachTicketsToOrders(data.orderList);

            setTimeslotOrders(ordersWithTickets);

            setIsLoading(false);
            
            return ordersWithTickets;
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching orders:', error);
            return [];
        }
    };

    // Simplified version - only attach tickets to orders, nothing else
    const attachTicketsToOrders = async (ordersList) => {
        if (!ordersList || !Array.isArray(ordersList)) return [];
        
        const ordersWithTickets = await Promise.all(
            ordersList.map(async (order) => {
                try {
                    // Get tickets for this order
                    const tickets = await GetTicketsOfOrder(baseUrl, "token", regularToken, order.id);
                    
                    // Return order with tickets attached
                    return { 
                        ...order, 
                        tickets: Array.isArray(tickets) ? tickets : [] 
                    };
                } catch (error) {
                    console.error(`Error fetching tickets for order ${order.id}:`, error);
                    // Return order without tickets if fetch fails
                    return { ...order, tickets: [] };
                }
            })
        );
        
        return ordersWithTickets;
    };

    const fetchEventById = async (eventId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchEventById");
            }
            const res = await GetEventById(baseUrl, "token", regularToken, eventId);
            return res;
        } catch (error) {
            console.error('Error fetching event by Id:', error);
        }
    };

    const fetchEventsThatAreInEventGroup = async (eventGroupId, language, eventBatchSize, numberToSkip, dateFrom, dateTo) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchEventsThatAreInEventGroup");
            }
            const res = await GetEventsThatAreInEventGroup(baseUrl, "token", regularToken, eventGroupId, language, eventBatchSize, numberToSkip, dateFrom, dateTo);
            return res;
        } catch (error) {
            console.error('Error fetching events that are in event group:', error);
        }
    };

    const fetchCountEventsThatAreInEventGroup = async (eventGroupId, dateFrom, dateTo) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchCountEventsThatAreInEventGroup");
            }
            const res = await CountEventsThatAreInEventGroup(baseUrl, "token", regularToken, eventGroupId, dateFrom, dateTo);
            return res;
        } catch (error) {
            console.error('Error fetching count events that are in event group:', error);
        }
    };

    const fetchTimeSlotsByEventId = async (eventId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchTimeSlotsByEventId");
            }
            const res = await GetTimeSlotsForEvent(baseUrl, "token", regularToken, eventId);
            return res;
        } catch (error) {
            console.error('Error fetching timeslots by eventId:', error);
        }
    };

    const fetchTicketCount = async (eventId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchTicketCount");
            }
            const res = await GetTicketSoldOfEvent(baseUrl, "token", regularToken, eventId);
            return res.data;
        } catch (error) {
            console.error('Error fetching ticket count:', error);
        }
    };

    const closeTimeslotPopup = () => {
        setDate("");
        setSelectedDate("");
    }

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setCurrentMonth(activeStartDate);

        // clear old values
        setSelectedDate("");
    };

    const onClickDay = (date) => {
        let selectedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const formattedDate = selectedDate.toISOString().split('T')[0];
        setSelectedDate(formattedDate);
    };

    const onTimeSlotClick = async (timeslot) => {
        // Prevent double bookings
        if (isBooking) {
            return;
        }

        setIsBooking(true);

        try {
            // Find ticketTypeId by matching externalTicketCode
            // Assume you have access to event ticketTypes via eventsWithDetails
            // Find the event details for this timeslot
            const event = eventsWithDetails.find(e => e.id === timeslot.eventId);
            const eventTicketTypes = event?.ticketTypes;

                // Map selectedTickets to ticketTypeId by matching externalTicketCode
                const selectedTicketsWithTicketTypeId = selectedTickets?.tickets.map(ticket => {
                    const matchedType = eventTicketTypes.find(tt => tt.externalTicketCode === ticket.externalTicketCode);
                    return {
                        ...ticket,
                        ticketTypeId: matchedType?.id,
                        timOfEntryFrom: timeslot?.beginTime,
                        timOfEntryTo: timeslot?.endTime
                    };
                }) || [];

                // Map selectedAddOns to ticketTypeId by matching externalTicketCode
                // AddOns should NOT get the timeslot info
                const selectedAddOnsWithTicketTypeId = selectedAddOns?.tickets.map(ticket => {
                    const matchedType = eventTicketTypes.find(tt => tt.externalTicketCode === ticket.externalTicketCode);
                    return {
                        ...ticket,
                        ticketTypeId: matchedType?.id
                    };
                }) || [];

                // cross selling City Rally
                let crossSellingTicketsWithTIcketTypeId = [];
                if (selectedTicketsCrossSelling && selectedTicketsCrossSelling.tickets.length > 0) {
                    crossSellingTicketsWithTIcketTypeId = await handleCrossSellingCityRally(eventGroupCityRallyId, currentMonth, selectedDate, selectedTicketsCrossSelling, timeslot);
                }

                // Concatenate all tickets
                const allSelectedTicketsWithTicketTypeId = [
                    ...selectedTicketsWithTicketTypeId,
                    ...selectedAddOnsWithTicketTypeId,
                    ...crossSellingTicketsWithTIcketTypeId
                ];

                await addTicketToBasket(allSelectedTicketsWithTicketTypeId, timeslot);
            } catch (error) {
                console.error("Error during booking:", error);
            } finally {
                setIsBooking(false);
            }
        };

    const handleCrossSellingCityRally = async (eventGroupCityRallyId, currentMonth, selectedDate, selectedTicketsCrossSelling, timeslot) => {
        // Helper function to add 90 minutes to a time string (HH:MM:SS format)
        const addMinutesToTime = (timeString, minutesToAdd) => {
            const [hours, minutes, seconds] = timeString.split(':').map(Number);
            const date = new Date();
            date.setHours(hours, minutes + minutesToAdd, seconds || 0);

            const newHours = String(date.getHours()).padStart(2, '0');
            const newMinutes = String(date.getMinutes()).padStart(2, '0');
            const newSeconds = String(date.getSeconds()).padStart(2, '0');

            return `${newHours}:${newMinutes}:${newSeconds}`;
        };

        // Calculate timeslot values with 20 minutes added
        //const timOfEntryFrom = timeslot?.beginTime ? addMinutesToTime(timeslot.beginTime, 20) : undefined;
        const timOfEntryFrom = timeslot?.endTime;
        const timOfEntryTo = timeslot?.endTime ? addMinutesToTime(timeslot.endTime, 20) : undefined;

        // Set dateFromISO to selectedDate and dateToISO to the next day
        const dateFromISO = selectedDate;
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const dateToISO = nextDay.toISOString().split('T')[0];

        const totalNumberOfEvents = await fetchCountEventsThatAreInEventGroup(eventGroupCityRallyId, dateFromISO, dateToISO);
        const eventBatchSize = 100;
        let events = [];

        // Fetch events in batches of 'eventBatchSize' until all events in the group are loaded.
        for (let i = 0; i <= totalNumberOfEvents; i += eventBatchSize) {
            const eventGroupEventsResponse = await fetchEventsThatAreInEventGroup(eventGroupCityRallyId, language, eventBatchSize, i, dateFromISO, dateToISO);
            events = events.concat(eventGroupEventsResponse?.events);
        }

        // Filter events to only those on the selectedDate
        const eventsOnSelectedDate = events.filter(event => {
            const eventDateUTC = new Date(event.start);
            return eventDateUTC.toISOString().split('T')[0] === selectedDate;
        });

        let crossSellingTickets = [];
        for (const event of eventsOnSelectedDate) {
            // Get ticket types for this event
            const ticketTypesResponse = await GetTicketTypesByEventId(baseUrl, "token", regularToken, event.id);
            const eventTicketTypes = (ticketTypesResponse?.ticketTypes || []).filter(tt => !!tt.externalTicketCode);

            // Map selectedTicketsCrossSelling to ticketTypeId by matching externalTicketCode
            const matchedTickets = selectedTicketsCrossSelling.tickets.map(ticket => {
                const matchedType = eventTicketTypes.find(tt => tt.externalTicketCode === ticket.externalTicketCode);
                if (matchedType) {
                    return {
                        ...ticket,
                        ticketTypeId: matchedType.id,
                        //timOfEntryFrom: timOfEntryFrom, commented out: rally tickets can be valid the entire day
                        //timOfEntryTo: timOfEntryTo
                    };
                }
                return null;
            }).filter(Boolean);

            crossSellingTickets = [...crossSellingTickets, ...matchedTickets];
        }
        return crossSellingTickets;
    };

    const addTicketToBasket = async (selectedTicketsWithTicketTypeId, timeslot) => {
        try {
            // Check if order still exists before adding tickets
            let currentOrder = await fetchOrderById(orderId);
            let targetOrderId = orderId;

            if (!currentOrder || !currentOrder.id) {
                // Order does not exist, create a new one
                const newOrder = await createOrder();
                if (!newOrder || !newOrder.id) {
                    console.error("Failed to create a new order.");
                    return;
                }
                setOrderId(newOrder.id);
                setOrder(newOrder);
                // Update sessionStorage with new orderId
                sessionStorage.setItem("OrderId", newOrder.id);
                targetOrderId = newOrder.id;
            } else {
                targetOrderId = currentOrder.id;
            }

            const ticketsToAdd = selectedTicketsWithTicketTypeId
                .filter(ticket => ticket.ticketTypeId && ticket.quantity > 0)
                .map(ticket => ({
                    ticketTypeId: ticket.ticketTypeId,
                    timOfEntryFrom: ticket.timOfEntryFrom,
                    timOfEntryTo: ticket.timOfEntryTo,
                    quantity: ticket.quantity,
                }));

            if (ticketsToAdd.length === 0) {
                console.warn("No valid tickets to add to basket.");
                return;
            }

            const addTicketsToOrderApiModel = {
                TicketsToAdd: ticketsToAdd
            };

            await AddTicketsToOrder(baseUrl, "token", regularToken, targetOrderId, addTicketsToOrderApiModel);

            document.body.classList.remove('no-scroll');

            navigate(`/${language}/summary`);
        } catch (error) {
            console.error("Failed to add tickets to basket:", error);
        }
    }

    const tileClassName = ({ date }) => {
        // Create a UTC date from the input
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Format the date as ISO string in UTC
        const formattedDate = utcDate.toISOString().split('T')[0];

        return eventDates.includes(formattedDate) ? 'bold-date' : 'disabled-date';
    };

    const formatTimeForCurrentTimeForTimeSlot = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const formatMonth = (locale, date) => {
        return date.toLocaleDateString(locale, { month: 'long' });
    };

    const formatSelectedDate = (dateString, locale) => {
        if (!dateString) return '';
        
        // Parse the ISO date string (YYYY-MM-DD) without timezone interpretation
        const [year, month, day] = dateString.split('-').map(Number);
        
        // Create a date object with explicit year, month (0-indexed), and day
        const date = new Date(year, month - 1, day);
        
        return date.toLocaleDateString(locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const mapEventStartTimes = () => {
        const currentTime = new Date(); // Get current date and time
        const currentTimeInZurich = toZonedTime(currentTime, timeZone);
        const formattedCurrentTimeZurich = formatTimeForCurrentTimeForTimeSlot(currentTimeInZurich);

        const yearObj = currentTimeInZurich.getFullYear();
        const monthObj = currentTimeInZurich.getMonth();
        const dayObj = currentTimeInZurich.getDate();

        // Compare the current date with event selected date
        const [yearStr, monthStr, dayStr] = selectedDate.split('-');

        const isSameDate = (
            yearObj === parseInt(yearStr) &&
            monthObj === parseInt(monthStr) - 1 &&
            dayObj === parseInt(dayStr)
        );

        // Calculate total quantity from selectedTickets
        const totalSelectedQuantity = (selectedTickets?.tickets || []).reduce((sum, ticket) => sum + (ticket.quantity || 0), 0);

        return (
            <>
                {timeSlots?.length > 0 && (
                    <div className="ticketing-timeslot-div">
                        <div className="row justify-content-center">
                            {timeSlots.map((timeSlot, index) => {
                                const ticketsLeft = timeSlot.totalAvailableTickets - timeSlot.totalTicketsSold;
                                const isSlotAvailable = ticketsLeft >= totalSelectedQuantity;
                                const isSlotInFuture = formattedCurrentTimeZurich < timeSlot.beginTime || !isSameDate;

                                return (
                                    isSlotInFuture && isSlotAvailable && (
                                        <div
                                            key={timeSlot.timeSlotId}
                                            className={`col-12 mb-1 d-flex flex-column align-items-center ${!isSlotAvailable ? "ticketing-disabled" : ""}`}
                                        >
                                            <div className="p-3 h-100 w-100">
                                                <div className="row justify-content-center text-center">
                                                    <div className="col-12">
                                                        <div>
                                                            {timeSlot.beginTime.split(':').slice(0, 2).join(':')} - {timeSlot.endTime.split(':').slice(0, 2).join(':')}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row justify-content-center text-center mt-2">
                                                    <div className="col-12">
                                                        <button
                                                            className="btn-custom"
                                                            disabled={!isSlotAvailable}
                                                            onClick={() => onTimeSlotClick(timeSlot)}
                                                        >
                                                            <span>{resources?._Book}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="row justify-content-center text-center mt-2">
                                                    <div className="col-12">
                                                        <div>
                                                            <small>{Math.max(0, ticketsLeft)} {ticketsLeft > 1 ? resources?._FreePlaces : resources?._FreePlacesSingular}</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                );
                            })}
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            <Header language={language} pagePath={`calendar`} pageTitle={resources?.CalendarTitle}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">
                <div className="container wrapper">

                    {/* Calendar */}
                    <div className="row">
                        <div className="col-md-12">
                            <div className={isLoading ? "is-hidden" : ""}>
                                <Calendar
                                    view="month"
                                    value={date}
                                    onChange={(newDate) => setDate(newDate)}
                                    tileClassName={tileClassName}
                                    onClickDay={onClickDay}
                                    locale={language}
                                    minDate={new Date(zurichDate.getFullYear(), defaultMonth, 1)}
                                    onActiveStartDateChange={handleActiveStartDateChange}
                                    formatMonthYear={formatMonth}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {selectedDate && (
                <div className="timeslot-overlay">
                    <div className="mx-auto timeslot-wrapper" style={{ maxWidth: "900px" }}>
                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={closeTimeslotPopup}
                                style={{
                                    all: "unset",
                                    cursor: "pointer",
                                    marginBottom: "10px",
                                    marginRight: "10px"
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="40"
                                    height="40"
                                    fill="none"
                                    stroke="#FF003C"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    viewBox="0 0 24 24"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className="text-center mb-4">
                            <h3 style={{ color: '#FF003C', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                {formatSelectedDate(selectedDate, language)}
                            </h3>

                            <span>{resources?.CalendarAvailableTimes}</span>
                        </div>

                        <div>{mapEventStartTimes()}</div>
                    </div>
                </div>
            )}

            <Footer language={language} />
        </>
    )
}

export default Step30_Calendar